"use client";

import type { ReactNode } from "react";
import FocusLock from "react-focus-lock";
import { RemoveScroll } from "react-remove-scroll";
import { Box } from "../Box/Box";
import { Divider } from "../Divider/Divider";
import { SafeAreaBottom } from "../SafeArea/SafeArea";
import { Text } from "../Text/Text";
import type { Color } from "../utils/colors";
import styles from "./Drawer.module.scss";
import { useDrawerPriority } from "./drawer-priority";

interface Props {
  title: string;
  children: ReactNode;
  open: boolean;
  onClose(): void;
  background?: Color;
  headerBackground?: Color;
  headerColor?: Color;
  zIndex?: number;
  /** While this drawer is open, all non-priority drawers stay closed. */
  priority?: boolean;
}

export function Drawer(props: Props): React.ReactNode {
  const {
    title,
    children,
    onClose,
    background = "white",
    zIndex,
    headerBackground,
    headerColor = "neutral-900",
  } = props;

  const open = useDrawerPriority(props.open, props.priority ?? false);

  return (
    <FocusLock group="drawer" disabled={!open} returnFocus={true}>
      <Box
        className={[styles.background, open ? styles.active : undefined]}
        width="full"
        height="full"
        position="fixed"
        display="flex"
        alignItems="flexEnd"
        justifyContent="center"
        onClick={onClose}
        style={{
          ...(zIndex ? { zIndex } : {}),
        }}
      >
        <RemoveScroll enabled={open}>
          <Box
            className={styles.container}
            borderRadius="standard"
            border="standard"
            borderColor="neutral-300"
            onClick={(e) => {
              e.stopPropagation();
            }}
            style={{
              cursor: "default",
            }}
          >
            <DrawerHeader
              title={title}
              background={headerBackground ?? background}
              color={headerColor}
            />
            <DrawerContent background={background}>{children}</DrawerContent>
            <SafeAreaBottom />
          </Box>
        </RemoveScroll>
      </Box>
    </FocusLock>
  );
}

function DrawerHeader(props: {
  title: string;
  background: Color;
  color: Color;
}): React.ReactNode {
  const { title, background, color } = props;

  return (
    <>
      <Box
        background={background}
        padding="3"
        display="flex"
        flexDirection="column"
        alignItems="center"
      >
        <Text
          font="accent"
          size="large"
          weight="semi-strong"
          color={color}
          align="center"
        >
          {title}
        </Text>
      </Box>
      <Divider />
    </>
  );
}

function DrawerContent(props: {
  children: ReactNode;
  background: Color;
}): React.ReactNode {
  const { children, background } = props;

  return (
    <Box background={background} paddingX="4" paddingTop="3" paddingBottom="5">
      {children}
    </Box>
  );
}
